import React from 'react';
import { bool, func, number, string } from 'prop-types';
import classNames from 'classnames';
import { useLocation } from 'react-router-dom';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import { userDisplayNameAsString } from '../../util/data';
import useIsMounted from '../../util/useIsMounted';
import config from '../../config';
import {
  Avatar,
  Counter,
  ExternalLink,
  LanguagePill,
  Logo,
  Menu,
  MenuAction,
  MenuContent,
  MenuItem,
  MenuLabel,
  NamedLink,
  Icon,
} from '../../components';
import { accountMenuRows } from '../Topbar/accountMenu';

import css from './TopbarDesktop.module.css';

const COMMUNITY_PAGES = ['CommunityPage', 'PostPage', 'EditPostPage'];

// "/signup/audience" → { isAuthPage, isAudienceSignup }
export const authPageInfo = pathname => {
  const isAuthPage = pathname.startsWith('/signup') || pathname === '/role-selection';
  const parts = pathname.split('/');
  const role = parts[parts.indexOf('signup') + 1];
  return { isAuthPage, isAudienceSignup: role === config.userRoles.audience };
};

// The link from one sign-up to the other: audience → creator (role selection), creator → audience
export const RoleSwitchLink = ({ className, isAudienceSignup }) =>
  isAudienceSignup ? (
    <NamedLink name="RoleSelectionPage" className={className}>
      <FormattedMessage id="TopbarDesktop.signupAsCreator" />
    </NamedLink>
  ) : (
    <NamedLink name="SignupPage" params={{ role: config.userRoles.audience }} className={className}>
      <FormattedMessage id="TopbarDesktop.signupAsAudience" />
    </NamedLink>
  );

/** The messages button from the boards: a speech bubble with the unread count on it. */
export const MessagesLink = ({ className, unreadCount }) => {
  const intl = useIntl();
  return (
    <NamedLink
      name="AudienceConversationsPage"
      className={classNames(css.messages, className)}
      title={intl.formatMessage({ id: 'TopbarDesktop.inboxLink' })}
    >
      <Icon name="message" size={20} />
      <span className={css.hidden}>
        <FormattedMessage id="TopbarDesktop.inboxLink" />
      </span>
      {unreadCount > 0 ? <Counter className={css.badge} count={unreadCount} /> : null}
    </NamedLink>
  );
};

/**
 * The person's own identity at the top of their menu (desktop popover and phone sheet).
 * Slice A puts the identities they can act as next to it.
 */
export const IdentityRow = ({ className, avatarClassName, currentUser }) => {
  const name = userDisplayNameAsString(currentUser, '');
  return (
    <span className={classNames(css.identity, className)}>
      <Avatar
        className={avatarClassName || css.identityAvatar}
        user={currentUser}
        disableProfileLink
      />
      <span className={css.identityText}>
        <span className={css.identityName}>{name}</span>
        <span className={css.faint}>
          <FormattedMessage id="TopbarDesktop.personalAccount" />
        </span>
      </span>
      <Icon name="check" className={css.check} />
    </span>
  );
};

const MenuRow = ({ row, unreadCount, onLogout }) => {
  const label = <FormattedMessage id={row.labelId} />;
  const toneClass = row.tone ? css[row.tone] : null;
  const extra =
    row.badge && unreadCount > 0 ? (
      <Counter className={css.after} count={unreadCount} />
    ) : row.hintId ? (
      <span className={classNames(css.after, css.faint, css.hint)}>
        <FormattedMessage id={row.hintId} />
      </span>
    ) : null;
  const icon = row.icon ? (
    <span className={css.glyph}>
      <Icon name={row.icon} />
    </span>
  ) : null;

  if (row.kind === 'external') {
    return (
      <ExternalLink className={css.row} href={row.href}>
        {label}
      </ExternalLink>
    );
  }
  if (row.kind === 'logout') {
    return (
      <MenuAction className={classNames(css.row, toneClass)} onClick={onLogout}>
        {label}
      </MenuAction>
    );
  }
  return (
    <MenuAction className={classNames(css.row, toneClass)} icon={icon} linkProps={row.linkProps}>
      {label}
      {extra}
    </MenuAction>
  );
};

/**
 * The identity button (46px pill with the avatar and a chevron, orange while open) and its
 * 340px menu, from the Landing and ActingAsMenu boards.
 */
const IdentityMenu = props => {
  const { currentUser, userRole, unreadCount, isBetaMode, onLogout } = props;
  const intl = useIntl();
  const rows = accountMenuRows({ userRole, isBetaMode, layout: 'popover' });
  return (
    <Menu contentPosition="left" useArrow={false}>
      <MenuLabel
        rootClassName={css.identityButton}
        isOpenClassName={css.identityButtonOpen}
        ariaLabel={intl.formatMessage({ id: 'Topbar.menuIcon' })}
      >
        <Avatar className={css.pillAvatar} user={currentUser} disableProfileLink />
        <Icon name="chevronDown" size={14} className={css.chevron} />
      </MenuLabel>
      <MenuContent className={css.menu}>
        {rows.map(row =>
          row.kind === 'separator' ? (
            <MenuItem key={row.key} type="separator" />
          ) : row.kind === 'caption' ? (
            <MenuItem key={row.key} type="caption">
              <FormattedMessage id={row.labelId} />
            </MenuItem>
          ) : row.kind === 'identity' ? (
            // Slice A: the acting-as rows and the disclaimer go here (ActingAsMenu board).
            <MenuItem key={row.key} type="caption" className={css.identityItem}>
              <IdentityRow currentUser={currentUser} />
            </MenuItem>
          ) : (
            <MenuItem key={row.key}>
              <MenuRow row={row} unreadCount={unreadCount} onLogout={onLogout} />
            </MenuItem>
          )
        )}
      </MenuContent>
    </Menu>
  );
};

/**
 * The public top bar on desktop (72px, Landing boards): logo, Our story / What is Attractr /
 * For Creators, the language pill, then Log in and Sign up for guests, or the messages button and
 * the identity menu for members. Community pages swap the links for "+ Create Post"; the sign-up
 * pages show the link to the other sign-up; community-only mode links back to Audience Lab.
 */
const TopbarDesktop = props => {
  const {
    rootClassName,
    className,
    contentClassName,
    logoClassName,
    currentUser,
    currentPage,
    isAuthenticated,
    isBetaMode,
    logoFormat,
    unreadCount,
    onLogout,
    onOpenLanguageModal,
  } = props;
  const intl = useIntl();
  const location = useLocation();
  const mounted = useIsMounted();

  // Members' controls render once in the page (the server renders the guest bar).
  const isMember = mounted && isAuthenticated;
  const userRole = currentUser?.attributes?.profile?.publicData?.role;
  const isCommunityPage = COMMUNITY_PAGES.includes(currentPage);
  const { isAuthPage, isAudienceSignup } = authPageInfo(location.pathname);
  const isGuestOnAuthPage = isAuthPage && !isAuthenticated;
  const showNavLinks = !isBetaMode && !isCommunityPage && !isGuestOnAuthPage;

  const navLink = (name, labelId) => (
    <NamedLink name={name} className={css.navLink} activeClassName={css.navLinkActive}>
      <FormattedMessage id={labelId} />
    </NamedLink>
  );

  return (
    <nav className={classNames(rootClassName || css.root, className)}>
      <div className={classNames(css.content, contentClassName)}>
        <NamedLink
          className={css.logoLink}
          name={isBetaMode ? 'CommunityPage' : 'LandingPage'}
          title={intl.formatMessage({ id: 'Topbar.logoIcon' })}
        >
          <Logo
            className={classNames(css.logo, logoClassName)}
            format={logoFormat || 'desktop'}
            alt={intl.formatMessage({ id: 'TopbarDesktop.logo' })}
          />
        </NamedLink>
        <div className={css.links}>
          {isBetaMode ? (
            <ExternalLink href="https://audiencelab.attractr.live" className={css.navLink}>
              <FormattedMessage id="TopbarDesktop.returnToAudienceLab" />
            </ExternalLink>
          ) : null}
          {isCommunityPage ? (
            <NamedLink name={currentUser ? 'NewPostPage' : 'LoginPage'} className={css.createPost}>
              <FormattedMessage id="TopbarDesktop.createPost" />
            </NamedLink>
          ) : null}
          {showNavLinks ? (
            <>
              {navLink('AboutPage', 'TopbarDesktop.about')}
              {navLink('AudiencePage', 'TopbarDesktop.whatIsAttractr')}
              {navLink('CreatorsPage', 'TopbarDesktop.forCreators')}
            </>
          ) : null}
          {!isBetaMode && isGuestOnAuthPage ? (
            <RoleSwitchLink className={css.navLink} isAudienceSignup={isAudienceSignup} />
          ) : null}
          {/* The same language pill as the footer: the globe, the current language and a caret */}
          <LanguagePill onClick={onOpenLanguageModal} />
          {isMember ? (
            <>
              {isBetaMode ? null : <MessagesLink unreadCount={unreadCount} />}
              <IdentityMenu
                currentUser={currentUser}
                userRole={userRole}
                unreadCount={unreadCount}
                isBetaMode={isBetaMode}
                onLogout={onLogout}
              />
            </>
          ) : (
            <>
              <NamedLink name="LoginPage" params={{ role: 'audience' }} className={css.login}>
                <FormattedMessage id="TopbarDesktop.login" />
              </NamedLink>
              <NamedLink
                name={isBetaMode ? 'CommunitySignupPage' : 'SignupPage'}
                params={isBetaMode ? undefined : { role: config.userRoles.audience }}
                className={css.signup}
              >
                <FormattedMessage id="TopbarDesktop.signup" />
              </NamedLink>
            </>
          )}
        </div>
      </div>
    </nav>
  );
};

TopbarDesktop.defaultProps = {
  rootClassName: null,
  className: null,
  contentClassName: null,
  logoClassName: null,
  currentUser: null,
  currentPage: null,
  isBetaMode: false,
  logoFormat: null,
  unreadCount: 0,
};

TopbarDesktop.propTypes = {
  rootClassName: string,
  className: string,
  contentClassName: string,
  logoClassName: string,
  currentUser: propTypes.currentUser,
  currentPage: string,
  isAuthenticated: bool.isRequired,
  isBetaMode: bool,
  logoFormat: string,
  unreadCount: number,
  onLogout: func.isRequired,
  onOpenLanguageModal: func.isRequired,
};

export default TopbarDesktop;
